/**
 * i18n — Lightweight internationalization for HAVN.
 *
 * Usage:
 *   import { I18nProvider, useTranslation, LANGUAGES } from '@/i18n';
 *
 *   // Wrap app:
 *   <I18nProvider><App /></I18nProvider>
 *
 *   // In any component:
 *   const { t, lang, setLang, languages } = useTranslation();
 *   t('greeting', { name: ' Margaret' })  → "Hi Margaret, I'm Haven..."
 *
 * Adding a language:
 *   1. Create  src/i18n/xx.json  (copy en.json as template)
 *   2. Add the code to LANGUAGES below
 *   3. That's it — the language appears in selectors automatically
 */

import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

// ── Language registry ─────────────────────────────────────────────
// To add a new language, import its JSON and add an entry here.
import en from './en.json';
import fr from './fr.json';

const BUNDLES = { en, fr };

// Ordered list of available languages (drives all UI selectors)
export const LANGUAGES = [
  { code: 'en', label: en._meta.label, flag: en._meta.flag },
  { code: 'fr', label: fr._meta.label, flag: fr._meta.flag },
];

const LANG_CODES = LANGUAGES.map(l => l.code);
const DEFAULT_LANG = 'en';
const STORAGE_KEY = 'havn_lang';

// ── Detect language ───────────────────────────────────────────────
function detectLanguage() {
  // 1. Saved preference
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && LANG_CODES.includes(saved)) return saved;
  } catch {}

  // 2. Browser language
  const nav = (navigator.language || navigator.userLanguage || '').toLowerCase();
  if (nav.startsWith('fr')) return 'fr';

  return DEFAULT_LANG;
}

// ── Interpolation ─────────────────────────────────────────────────
// Replace {{key}} placeholders:  t('greeting', { name: ' Margaret' })
function interpolate(template, vars) {
  if (!vars || typeof template !== 'string') return template;
  return template.replace(/\{\{(\w+)\}\}/g, (_, key) => {
    return vars[key] !== undefined ? vars[key] : `{{${key}}}`;
  });
}

// ── Context ───────────────────────────────────────────────────────
const I18nContext = createContext(null);

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(detectLanguage);

  const setLang = useCallback((code) => {
    if (!LANG_CODES.includes(code)) return;
    setLangState(code);
    try { localStorage.setItem(STORAGE_KEY, code); } catch {}
    // Also update html lang attribute for accessibility
    document.documentElement.lang = code;
  }, []);

  // Set html lang + axios header on every language change
  useEffect(() => {
    document.documentElement.lang = lang;
    axios.defaults.headers.common['x-user-lang'] = lang;
  }, [lang]);

  const bundle = BUNDLES[lang] || BUNDLES[DEFAULT_LANG];
  const fallback = BUNDLES[DEFAULT_LANG];

  const t = useCallback((key, vars) => {
    const template = bundle[key] ?? fallback[key] ?? key;
    return interpolate(template, vars);
  }, [bundle, fallback]);

  const value = useMemo(() => ({
    lang,
    setLang,
    t,
    languages: LANGUAGES,
  }), [lang, setLang, t]);

  return (
    <I18nContext.Provider value={value}>
      {children}
    </I18nContext.Provider>
  );
}

export function useTranslation() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useTranslation must be used within I18nProvider');
  return ctx;
}
